import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Tab,
    Tabs,
    Pagination,
    useTheme,
    Typography,
    Alert,
    Card,
    CardContent,
    CircularProgress,
    Collapse,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublishIcon from '@mui/icons-material/Publish';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useSnackbar } from 'notistack';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const ClassLogBatch = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();

    // Tab state
    const [currentTab, setCurrentTab] = useState(0);

    // Data states
    const [stats, setStats] = useState({
        pending: 0, pendingReady: 0, pendingMissing: 0, recoveredCount: 0,
        published: 0, nextPublishAt: null, secondsUntilPublish: 0, batchWindow: null
    });
    const [pendingLogs, setPendingLogs] = useState([]);
    const [batchWindow, setBatchWindow] = useState(null);
    const [historyLogs, setHistoryLogs] = useState([]);

    // Loading states
    const [statsLoading, setStatsLoading] = useState(true);
    const [logsLoading, setLogsLoading] = useState(false);

    // Pagination for published
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });

    // Countdown state
    const [countdown, setCountdown] = useState('');
    const countdownRef = useRef(null);

    // Publishing state
    const [publishing, setPublishing] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [publishingSingle, setPublishingSingle] = useState(null);

    // Expanded cards (for showing teacher evals)
    const [expandedCards, setExpandedCards] = useState({});

    // Expanded courses (for grouping)
    const [expandedCourses, setExpandedCourses] = useState({});

    // Search filters
    const [searchCourseName, setSearchCourseName] = useState('');
    const [searchCourseCode, setSearchCourseCode] = useState('');
    const [searchTeacher, setSearchTeacher] = useState('');
    const [searchStudent, setSearchStudent] = useState('');

    // Edit state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingLog, setEditingLog] = useState(null);
    const [editedAiEval, setEditedAiEval] = useState('');
    const [editedAiEvalEn, setEditedAiEvalEn] = useState('');
    const [saving, setSaving] = useState(false);

    // Regenerate state
    const [regenerating, setRegenerating] = useState(null);
    const pollIntervalRef = useRef(null);

    // Unpublish state
    const [unpublishing, setUnpublishing] = useState(null);

    const [error, setError] = useState('');

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    // Fetch stats
    const fetchStats = async () => {
        try {
            setStatsLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/stats`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setStats(response.data);
            setStatsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching stats:', error);
                setError('Failed to load statistics');
                setStatsLoading(false);
            }
        }
    };

    // Fetch pending logs (today's batch only)
    const fetchPendingLogs = async () => {
        try {
            setLogsLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/pending`,
                { headers: { Authorization: getAuthHeader() } }
            );
            const allLogs = response.data.logs || [];
            setPendingLogs(allLogs);
            setBatchWindow(response.data.batchWindow || null);
            setLogsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching pending logs:', error);
                setError('Failed to load pending logs');
                setLogsLoading(false);
            }
        }
    };

    // Fetch history logs (past batches - both published and missed)
    const fetchHistoryLogs = async (page = 1) => {
        try {
            setLogsLoading(true);
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/history?page=${page}&limit=${pagination.limit}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setHistoryLogs(response.data.logs || []);
            setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
            setLogsLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error fetching history logs:', error);
                setError('Failed to load history logs');
                setLogsLoading(false);
            }
        }
    };

    // Publish all pending
    const handlePublishAll = async () => {
        try {
            setPublishing(true);
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/publish`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar(`${response.data.publishedCount}개의 수업 로그가 발행되었습니다.`, { variant: 'success' });
            setPublishDialogOpen(false);
            // Refresh data
            fetchStats();
            fetchPendingLogs();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar('발행 중 오류가 발생했습니다.', { variant: 'error' });
            }
        } finally {
            setPublishing(false);
        }
    };

    // Publish single log
    const handlePublishSingle = async (logId) => {
        try {
            setPublishingSingle(logId);
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/publish/${logId}`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('수업 로그가 발행되었습니다.', { variant: 'success' });
            // Refresh data
            fetchStats();
            fetchPendingLogs();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar('발행 중 오류가 발생했습니다.', { variant: 'error' });
            }
        } finally {
            setPublishingSingle(null);
        }
    };

    // Open edit dialog
    const handleEditOpen = (log) => {
        setEditingLog(log);
        setEditedAiEval(log.ai_eval || '');
        setEditedAiEvalEn(log.ai_eval_en || '');
        setEditDialogOpen(true);
    };

    // Save edited AI eval
    const handleSaveEdit = async () => {
        if (!editingLog) return;
        try {
            setSaving(true);
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/${editingLog.id}`,
                { ai_eval: editedAiEval, ai_eval_en: editedAiEvalEn },
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('AI 피드백이 수정되었습니다.', { variant: 'success' });
            setEditDialogOpen(false);
            setEditingLog(null);
            fetchPendingLogs();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar('수정 중 오류가 발생했습니다.', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    // Regenerate AI eval
    const handleRegenerate = async (logId) => {
        try {
            setRegenerating(logId);
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/${logId}/regenerate`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('AI 피드백 재생성이 요청되었습니다. 잠시 후 자동 갱신됩니다.', { variant: 'info' });

            // Start polling to check for regeneration completion
            let pollCount = 0;
            const maxPolls = 30; // Poll for up to 30 seconds
            pollIntervalRef.current = setInterval(async () => {
                pollCount++;
                try {
                    const response = await Axios.get(
                        `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/pending`,
                        { headers: { Authorization: getAuthHeader() } }
                    );
                    const logs = response.data.logs || [];
                    const updatedLog = logs.find(l => l.id === logId);

                    // If log has ai_eval again or max polls reached, stop polling
                    if ((updatedLog && updatedLog.ai_eval) || pollCount >= maxPolls) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                        setRegenerating(null);
                        setPendingLogs(logs);
                        fetchStats();
                        if (updatedLog && updatedLog.ai_eval) {
                            enqueueSnackbar('AI 피드백가 재생성되었습니다.', { variant: 'success' });
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                }
            }, 1000);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar(error.response?.data?.error || '재생성 요청 중 오류가 발생했습니다.', { variant: 'error' });
            }
            setRegenerating(null);
        }
    };

    // Unpublish a log (move back to pending)
    const handleUnpublish = async (logId) => {
        try {
            setUnpublishing(logId);
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/admin/class-log-batch/${logId}/unpublish`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('발행이 취소되었습니다. 발행 대기 탭으로 이동되었습니다.', { variant: 'success' });
            // Refresh data
            fetchStats();
            fetchHistoryLogs(pagination.page);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                enqueueSnackbar(error.response?.data?.error || '발행 취소 중 오류가 발생했습니다.', { variant: 'error' });
            }
        } finally {
            setUnpublishing(null);
        }
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    // Format countdown
    const formatCountdown = (seconds) => {
        if (seconds <= 0) return '발행 진행 중...';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours}시간 ${minutes}분 ${secs}초`;
    };

    // Update countdown every second
    useEffect(() => {
        if (stats.nextPublishAt) {
            const updateCountdown = () => {
                const now = dayjs();
                const target = dayjs(stats.nextPublishAt);
                const diff = target.diff(now, 'second');
                setCountdown(formatCountdown(Math.max(0, diff)));
            };

            updateCountdown();
            countdownRef.current = setInterval(updateCountdown, 1000);

            return () => {
                if (countdownRef.current) {
                    clearInterval(countdownRef.current);
                }
            };
        }
    }, [stats.nextPublishAt]);

    // Initialize
    useEffect(() => {
        localStorage.setItem('selectedMenu', 'classLogBatch');
        fetchStats();
    }, []);

    // Load logs when tab changes
    useEffect(() => {
        if (currentTab === 0) {
            fetchPendingLogs();
        } else if (currentTab === 1) {
            fetchHistoryLogs(1);
        }
    }, [currentTab]);

    // Get student display name
    const getStudentName = (log) => {
        const student = log.student;
        if (!student) return 'Unknown';
        const parts = [];
        if (student.first_name) parts.push(student.first_name);
        if (student.korean_name) parts.push(`(${student.korean_name})`);
        if (student.last_name) parts.push(student.last_name);
        return parts.join(' ') || `Student ${student.id}`;
    };

    // Get course name
    const getCourseName = (log) => {
        return log.event?.course?.course_title || 'Unknown Course';
    };

    // Format date in ET
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dayjs(dateStr).tz('America/New_York').format('YYYY/MM/DD (ddd) HH:mm');
    };

    // Format datetime in ET
    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dayjs(dateStr).tz('America/New_York').format('YYYY/MM/DD HH:mm');
    };

    // Toggle card expansion
    const toggleExpand = (logId) => {
        setExpandedCards(prev => ({ ...prev, [logId]: !prev[logId] }));
    };

    if (statsLoading) {
        return <Loading />;
    }

    // Group logs by course
    const groupLogsByCourse = (logs) => {
        const grouped = {};
        logs.forEach(log => {
            const courseId = log.event?.course?.id || 'unknown';
            const courseName = log.event?.course?.course_title || 'Unknown Course';
            const courseCode = log.event?.course?.code || '';
            if (!grouped[courseId]) {
                grouped[courseId] = {
                    courseName,
                    courseCode,
                    courseLogs: []
                };
            }
            grouped[courseId].courseLogs.push(log);
        });
        return grouped;
    };

    // Toggle course expansion (default is expanded, so undefined means true)
    const toggleCourseExpand = (courseId) => {
        setExpandedCourses(prev => ({
            ...prev,
            [courseId]: prev[courseId] === undefined ? false : !prev[courseId]
        }));
    };

    // Check if all courses are expanded for current tab
    const areAllExpanded = () => {
        const logs = currentTab === 0 ? pendingLogs : historyLogs;
        const tabType = currentTab === 0 ? 'pending' : 'history';
        const grouped = groupLogsByCourse(logs);
        return Object.keys(grouped).every(courseId => expandedCourses[`${tabType}_${courseId}`] !== false);
    };

    // Toggle all courses expand/collapse for current tab
    const toggleAllCourses = () => {
        const logs = currentTab === 0 ? pendingLogs : historyLogs;
        const tabType = currentTab === 0 ? 'pending' : 'history';
        const grouped = groupLogsByCourse(logs);
        const shouldCollapse = areAllExpanded();
        const newState = {};
        Object.keys(grouped).forEach(courseId => {
            newState[`${tabType}_${courseId}`] = !shouldCollapse;
        });
        setExpandedCourses(prev => ({ ...prev, ...newState }));
    };

    // Unified card renderer for all tabs
    const renderGroupedCards = (logs, tabType, emptyMessage, chipColor) => {
        if (logsLoading) {
            return (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            );
        }

        if (logs.length === 0) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                </Paper>
            );
        }

        // Filter logs by all search criteria
        const hasAnyFilter = searchCourseName || searchCourseCode || searchTeacher || searchStudent;
        const filteredLogs = hasAnyFilter ? logs.filter(log => {
            const courseName = log.event?.course?.course_title || '';
            const courseCode = log.event?.course?.code || '';
            const teacherName = log.event?.teacher
                ? `${log.event.teacher.first_name || ''} ${log.event.teacher.last_name || ''}`.trim()
                : '';
            const studentName = log.student
                ? `${log.student.first_name || ''} ${log.student.last_name || ''} ${log.student.korean_name || ''}`.trim()
                : '';

            const matchesCourseName = !searchCourseName || courseName.toLowerCase().includes(searchCourseName.toLowerCase());
            const matchesCourseCode = !searchCourseCode || courseCode.toLowerCase().includes(searchCourseCode.toLowerCase());
            const matchesTeacher = !searchTeacher || teacherName.toLowerCase().includes(searchTeacher.toLowerCase());
            const matchesStudent = !searchStudent || studentName.toLowerCase().includes(searchStudent.toLowerCase());

            return matchesCourseName && matchesCourseCode && matchesTeacher && matchesStudent;
        }) : logs;

        if (filteredLogs.length === 0 && hasAnyFilter) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">검색 결과가 없습니다.</Typography>
                </Paper>
            );
        }

        const groupedLogs = groupLogsByCourse(filteredLogs);
        const filteredEntries = Object.entries(groupedLogs);

        return (
            <Box display="flex" flexDirection="column" gap={1}>
                {filteredEntries.map(([courseId, { courseName, courseCode, courseLogs }]) => {
                    const expandKey = `${tabType}_${courseId}`;
                    return (
                        <Accordion
                            key={courseId}
                            expanded={expandedCourses[expandKey] !== false}
                            onChange={() => toggleCourseExpand(expandKey)}
                            sx={{ bgcolor: colors.primary[600] }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    {courseCode && (
                                        <Chip
                                            label={courseCode}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    )}
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {courseName}
                                    </Typography>
                                    <Chip
                                        label={`${courseLogs.length}개`}
                                        size="small"
                                        color={chipColor}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <Box display="flex" flexDirection="column" gap={2} p={2}>
                                    {courseLogs.map((log) => renderLogCard(log, tabType))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        );
    };

    // Render individual log card based on tab type
    const renderLogCard = (log, tabType) => {
        // Use unique key: prefer log.id, fallback to student_id + event_id
        const uniqueId = log.id || `${log.student_id}_${log.event_id}`;
        const cardKey = `${tabType}_${uniqueId}`;
        const isPending = tabType === 'pending';
        const isHistory = tabType === 'history';
        const hasCompleteTeacherEval = (log.eval_1 && log.eval_1.trim()) && (log.eval_2 && log.eval_2.trim());

        return (
            <Card key={uniqueId} variant="outlined" sx={
                log.status === 'missing_teacher_eval' ? { borderColor: 'warning.main', borderWidth: 2 }
                : log.status === 'missing_ai_eval' ? { borderColor: 'info.main', borderWidth: 2 }
                : {}
            }>
                {/* Header */}
                <Box sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: log.status === 'missing_teacher_eval' ? colors.redAccent[700]
                           : log.status === 'missing_ai_eval' ? colors.blueAccent?.[700] || colors.primary[600]
                           : colors.primary[700],
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2
                }}>
                    {/* Left: Student info */}
                    <Box sx={{ minWidth: 200 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {getStudentName(log)}
                            </Typography>
                            {isPending && log.isOverdue && (
                                <Chip label="지연됨" color="warning" size="small" />
                            )}
                            {isPending && log.status === 'missing_teacher_eval' && (
                                <Chip label="선생님 피드백 미작성" color="warning" size="small" />
                            )}
                            {isPending && log.status === 'missing_ai_eval' && (
                                <Chip label="AI 피드백 생성 중" color="info" size="small" />
                            )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            모듈: {log.event?.title || '(없음)'} - {formatDate(log.event?.start)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                            선생님 피드백: {log.eval_1 ? '✓' : '✗'} Eval 1 | {log.eval_2 ? '✓' : '✗'} Eval 2
                        </Typography>
                        {isPending && log.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                                작성: {formatDateTime(log.createdAt)} ET
                            </Typography>
                        )}
                        {isHistory && log.ai_eval_published_at && (
                            <Typography variant="caption" color="text.secondary" component="div">
                                발행일자: {formatDateTime(log.ai_eval_published_at)} ET
                            </Typography>
                        )}
                    </Box>
                    {/* Middle: Teacher info */}
                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        {log.event?.teacher ? (
                            <>
                                <Typography variant="body2" fontWeight="medium">
                                    선생님: {log.event.teacher.first_name} {log.event.teacher.last_name}
                                </Typography>
                                {log.event.teacher.phone && (
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        📞 {log.event.teacher.phone}
                                    </Typography>
                                )}
                                {log.event.teacher.email && (
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        ✉️ {log.event.teacher.email}
                                    </Typography>
                                )}
                                {log.event.teacher.personal_email && (
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        ✉️ {log.event.teacher.personal_email}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                선생님 정보 없음
                            </Typography>
                        )}
                    </Box>
                    {/* Right: Actions */}
                    <Box display="flex" gap={1} alignItems="center">
                        {isPending && (
                            <>
                                {log.status === 'missing_teacher_eval' ? (
                                    // Missing teacher eval - show message only
                                    <Typography variant="body2" color="warning.main">
                                        선생님 피드백 대기 중
                                    </Typography>
                                ) : log.status === 'missing_ai_eval' ? (
                                    // Has teacher eval but no AI eval - show generate button
                                    <Tooltip title="AI 피드백 생성">
                                        <span>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="info"
                                                startIcon={regenerating === log.id ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                                                onClick={(e) => { e.stopPropagation(); log.id && handleRegenerate(log.id); }}
                                                disabled={regenerating === log.id || !log.id}
                                            >
                                                AI 피드백 생성
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    // Ready - has AI eval, show edit, regenerate, publish
                                    <>
                                        <Tooltip title="AI 피드백 재생성">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="warning"
                                                startIcon={regenerating === log.id ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                                                onClick={(e) => { e.stopPropagation(); handleRegenerate(log.id); }}
                                                disabled={regenerating === log.id || !hasCompleteTeacherEval}
                                            >
                                                재생성
                                            </Button>
                                        </Tooltip>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={(e) => { e.stopPropagation(); handleEditOpen(log); }}
                                        >
                                            수정
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="small"
                                            startIcon={publishingSingle === log.id ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                                            onClick={(e) => { e.stopPropagation(); handlePublishSingle(log.id); }}
                                            disabled={publishingSingle === log.id}
                                        >
                                            수동 발행
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                        {isHistory && (
                            <>
                                {log.status === 'published' ? (
                                    <>
                                        <Chip label="발행됨" color="success" size="small" />
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            size="small"
                                            startIcon={unpublishing === log.id ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
                                            onClick={(e) => { e.stopPropagation(); handleUnpublish(log.id); }}
                                            disabled={unpublishing === log.id}
                                        >
                                            발행 취소
                                        </Button>
                                    </>
                                ) : log.status === 'missing_teacher_eval' ? (
                                    <Chip label="선생님 피드백 미작성" color="warning" size="small" />
                                ) : log.status === 'missing_ai_eval' ? (
                                    <Chip label="AI 피드백 없음" color="info" size="small" />
                                ) : (
                                    <Chip label="미발행" color="default" size="small" />
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Content */}
                <CardContent sx={{ py: 2 }}>
                    {log.status === 'missing_teacher_eval' ? (
                        // Missing teacher eval: show what's written and what's missing
                        <>
                            {log.eval_1 ? (
                                <Box mb={log.eval_2 ? 2 : 0}>
                                    <Typography variant="caption" color="text.secondary">선생님 피드백 1:</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.eval_1}</Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="warning.main">선생님 피드백 1 미작성</Typography>
                            )}
                            {log.eval_2 ? (
                                <Box mt={2}>
                                    <Typography variant="caption" color="text.secondary">선생님 피드백 2:</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.eval_2}</Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="warning.main" mt={1}>선생님 피드백 2 미작성</Typography>
                            )}
                            <Alert severity="info" sx={{ mt: 2 }}>
                                선생님 피드백(Eval 1, Eval 2)가 모두 작성되면 AI 피드백를 생성할 수 있습니다.
                            </Alert>
                        </>
                    ) : log.status === 'missing_ai_eval' ? (
                        // Has teacher eval but missing AI eval: show teacher evals and generation status
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                선생님 피드백가 작성되었습니다. AI 피드백 생성을 클릭하세요.
                            </Alert>
                            <Box sx={{ p: 2, bgcolor: colors.primary[800], borderRadius: 1 }}>
                                {log.eval_1 && (
                                    <Box mb={log.eval_2 ? 2 : 0}>
                                        <Typography variant="caption" color="text.secondary">선생님 피드백 1:</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.eval_1}</Typography>
                                    </Box>
                                )}
                                {log.eval_2 && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">선생님 피드백 2:</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.eval_2}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    ) : (
                        // Pending (with ai_eval) or Published: show AI eval with collapsible teacher evals
                        <>
                            <Box display="flex" gap={2}>
                                {/* Korean AI Eval */}
                                <Box flex={1}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        🇰🇷 한국어
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.7,
                                            color: 'text.primary',
                                            p: 1.5,
                                            bgcolor: colors.primary[800],
                                            borderRadius: 1,
                                        }}
                                    >
                                        {log.ai_eval || '(AI 피드백 없음)'}
                                    </Typography>
                                </Box>
                                {/* English AI Eval */}
                                <Box flex={1}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        🇺🇸 English
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            lineHeight: 1.7,
                                            color: 'text.primary',
                                            p: 1.5,
                                            bgcolor: colors.primary[800],
                                            borderRadius: 1,
                                        }}
                                    >
                                        {log.ai_eval_en || '(No AI feedback)'}
                                    </Typography>
                                </Box>
                            </Box>

                            {(log.eval_1 || log.eval_2) && (
                                <Box mt={2}>
                                    <Button
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); toggleExpand(cardKey); }}
                                        endIcon={expandedCards[cardKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        선생님 피드백 보기
                                    </Button>
                                    <Collapse in={expandedCards[cardKey]}>
                                        <Box sx={{ mt: 1, p: 2, bgcolor: colors.primary[800], borderRadius: 1 }}>
                                            {log.eval_1 && (
                                                <Box mb={log.eval_2 ? 2 : 0}>
                                                    <Typography variant="caption" color="text.secondary">선생님 피드백 1:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.eval_1}</Typography>
                                                </Box>
                                            )}
                                            {log.eval_2 && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">선생님 피드백 2:</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{log.eval_2}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title="학부모 포탈 수업 로그" subtitle="AI 수업 피드백 발행 관리" />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Stats Cards */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                {/* Next Publish Time Card */}
                <Card sx={{ minWidth: 250, flex: 1 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <ScheduleIcon color="primary" />
                            <Typography variant="subtitle2" color="text.secondary">
                                다음 자동 발행
                            </Typography>
                        </Box>
                        <Typography variant="h4" color="primary">
                            {countdown || '계산 중...'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            매일 오후 3시 (미국 동부시간)
                        </Typography>
                        {batchWindow && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                발행 대상: {batchWindow.start} ~ {batchWindow.end} ET
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Pending Count Card */}
                <Card sx={{ minWidth: 220 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <PendingActionsIcon color="info" />
                            <Typography variant="subtitle2" color="text.secondary">
                                발행 대기 (준비 완료)
                            </Typography>
                        </Box>
                        <Typography variant="h3" color="info.main">
                            {stats.pendingReady}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                            선생님 피드백 미작성: {stats.pendingMissing}
                        </Typography>
                        {stats.recoveredCount > 0 && (
                            <Typography variant="caption" color="warning.main" component="div">
                                지연된 로그: {stats.recoveredCount}
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Published Count Card */}
                <Card sx={{ minWidth: 180 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <CheckCircleIcon color="success" />
                            <Typography variant="subtitle2" color="text.secondary">
                                발행 완료
                            </Typography>
                        </Box>
                        <Typography variant="h3" color="success.main">
                            {stats.published}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Tabs and Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Tabs
                    value={currentTab}
                    onChange={(_, newValue) => setCurrentTab(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label={`발행 대기 (${stats.pendingReady}/${stats.pending})`} />
                    <Tab label={`지난 로그 (${stats.published})`} />
                </Tabs>

                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <TextField
                        size="small"
                        placeholder="코드"
                        value={searchCourseCode}
                        onChange={(e) => setSearchCourseCode(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 100 }}
                    />
                    <TextField
                        size="small"
                        placeholder="수업명"
                        value={searchCourseName}
                        onChange={(e) => setSearchCourseName(e.target.value)}
                        sx={{ width: 140 }}
                    />
                    <TextField
                        size="small"
                        placeholder="선생님"
                        value={searchTeacher}
                        onChange={(e) => setSearchTeacher(e.target.value)}
                        sx={{ width: 100 }}
                    />
                    <TextField
                        size="small"
                        placeholder="학생"
                        value={searchStudent}
                        onChange={(e) => setSearchStudent(e.target.value)}
                        sx={{ width: 100 }}
                    />
                    <Tooltip title={areAllExpanded() ? "전체 접기" : "전체 펼치기"}>
                        <IconButton onClick={toggleAllCourses} color="primary" size="small">
                            {areAllExpanded() ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="새로고침">
                        <IconButton
                            onClick={() => {
                                fetchStats();
                                if (currentTab === 0) fetchPendingLogs();
                                else fetchHistoryLogs(pagination.page);
                            }}
                            color="primary"
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {currentTab === 0 && stats.pendingReady > 0 && (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<PublishIcon />}
                            onClick={() => setPublishDialogOpen(true)}
                        >
                            전체 수동 발행 ({stats.pendingReady})
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Content */}
            {currentTab === 0 && (
                <>
                    {/* Recovered Logs (late teacher submissions from past batches) */}
                    {pendingLogs.filter(log => log.isOverdue).length > 0 && (
                        <>
                            <Typography variant="h6" mb={1} color="warning.main">
                                지연된 로그 (이전 배치에서 복구됨)
                            </Typography>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                마감 후 피드백이 작성된 이전 수업 로그입니다.
                            </Alert>
                            {renderGroupedCards(pendingLogs.filter(log => log.isOverdue), 'pending', '', 'warning')}
                            <Box mt={3} />
                        </>
                    )}
                    {/* Today's Batch */}
                    <Typography variant="h6" mb={1} color="info.main">
                        이번 발행 대상
                    </Typography>
                    {renderGroupedCards(pendingLogs.filter(log => !log.isOverdue), 'pending', '발행 대기 중인 로그가 없습니다.', 'info')}
                </>
            )}
            {currentTab === 1 && renderGroupedCards(historyLogs, 'history', '지난 로그가 없습니다.', 'success')}

            {/* Pagination for history */}
            {currentTab === 1 && pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={pagination.totalPages}
                        page={pagination.page}
                        onChange={(_, page) => {
                            setPagination(prev => ({ ...prev, page }));
                            fetchHistoryLogs(page);
                        }}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Publish All Dialog */}
            <Dialog
                open={publishDialogOpen}
                onClose={() => setPublishDialogOpen(false)}
            >
                <DialogTitle>전체 수동 발행 확인</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {stats.pendingReady}개의 AI 수업 피드백를 지금 발행하시겠습니까?
                        <br /><br />
                        발행된 피드백는 즉시 학부모 포탈에서 확인 가능합니다.
                        {stats.pendingMissing > 0 && (
                            <>
                                <br /><br />
                                ※ 선생님 피드백 미완료 {stats.pendingMissing}건은 발행 대상에서 제외됩니다.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPublishDialogOpen(false)} disabled={publishing}>
                        취소
                    </Button>
                    <Button
                        onClick={handlePublishAll}
                        color="success"
                        variant="contained"
                        disabled={publishing}
                        startIcon={publishing ? <CircularProgress size={18} /> : <PublishIcon />}
                    >
                        {publishing ? '발행 중...' : '수동 발행'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => !saving && setEditDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    AI 피드백 수정
                    {editingLog && (
                        <Typography variant="body2" color="text.secondary">
                            {getStudentName(editingLog)} • {getCourseName(editingLog)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" gap={2} mt={1}>
                        <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                🇰🇷 한국어
                            </Typography>
                            <TextField
                                autoFocus
                                multiline
                                rows={12}
                                fullWidth
                                variant="outlined"
                                value={editedAiEval}
                                onChange={(e) => setEditedAiEval(e.target.value)}
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                🇺🇸 English
                            </Typography>
                            <TextField
                                multiline
                                rows={12}
                                fullWidth
                                variant="outlined"
                                value={editedAiEvalEn}
                                onChange={(e) => setEditedAiEvalEn(e.target.value)}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
                        취소
                    </Button>
                    <Button
                        onClick={handleSaveEdit}
                        color="primary"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} /> : null}
                    >
                        {saving ? '저장 중...' : '저장'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClassLogBatch;
